// @view/workspace/DocumentCreation.tsx
import React from "react";
import type { Ok } from "@ps-bridge/ok";
import type { IOts } from "@ps-bridge/interop";
import * as IO from "@ps-bridge/interop/io";
import type { StateUpdate } from "@ps-bridge/capabilitiesTypes";
import type { DocumentMetadata } from "@ps-bridge/models/core/dataContracts";
import type { DocumentID } from "@ps-bridge/models/brands/documentID";
import DocumentCreationFormView from "@view/workspace/document-creation/DocumentCreationForm.view";
import DocumentCreationReviewView from "@view/workspace/document-creation/DocumentCreationReview.view";
import DocumentCreationSnapshotView from "@view/workspace/document-creation/DocumentCreationSnapshot.view";
import type { DocumentCreationViewProps } from "@ps-bridge/contracts/documentCreation";
import DocumentCreationPanel, {
  type DocumentCreationBackPhase,
  type DocumentCreationStep,
  type DocumentCreationStepPhase,
} from "@view/panels/workspace/document-creation/DocumentCreation.panel";

export type DocumentCreationSourceSelectionProps = {
  selectedSourceDocumentIds: DocumentID[];
  selectedSourceDocuments: DocumentMetadata[];
  onToggleSourceDocument: (documentId: DocumentID) => Ok;
  onUploadSourceDocument: (file: File) => Ok;
  isSourceDocumentUploading: boolean;
  sourceDocumentUploadError: string;
};

export type DocumentCreationInteractionProps = {
  titleValue: string;
  styleValue: string;
  outlineValue: string;
  outlineLines: string[];
  onTitleChange: StateUpdate<string>;
  onStyleChange: StateUpdate<string>;
  onOutlineChange: StateUpdate<string>;
  stepIndex: number;
  stepPhase: DocumentCreationStepPhase;
  backPhase: DocumentCreationBackPhase;
  steps: DocumentCreationStep[];
  nextActionLabel: string;
  nextAction: IOts<Ok>;
  nextDisabled: boolean;
  onBack: IOts<Ok>;
};

type DocumentCreationProps =
  DocumentCreationViewProps &
  DocumentCreationSourceSelectionProps &
  DocumentCreationInteractionProps;

const DocumentCreation: React.FC<DocumentCreationProps> = ({
  availableDocuments,
  titleValue,
  styleValue,
  outlineValue,
  outlineLines,
  onTitleChange,
  onStyleChange,
  onOutlineChange,
  stepIndex,
  stepPhase,
  backPhase,
  steps,
  nextActionLabel,
  nextAction,
  nextDisabled,
  onBack,
  selectedSourceDocumentIds,
  selectedSourceDocuments,
  onToggleSourceDocument,
  onUploadSourceDocument,
  isSourceDocumentUploading,
  sourceDocumentUploadError,
}) => {
  const runPanelIO = (effect: IOts<Ok>): Ok => IO.run(effect);
  const formView = (
    <DocumentCreationFormView
      titleValue={titleValue}
      styleValue={styleValue}
      outlineValue={outlineValue}
      onTitleChange={onTitleChange}
      onStyleChange={onStyleChange}
      onOutlineChange={onOutlineChange}
      availableDocuments={availableDocuments}
      selectedSourceDocumentIds={selectedSourceDocumentIds}
      onToggleSourceDocument={onToggleSourceDocument}
      onUploadSourceDocument={onUploadSourceDocument}
      isSourceDocumentUploading={isSourceDocumentUploading}
      sourceDocumentUploadError={sourceDocumentUploadError}
    />
  );
  const reviewView = (
    <DocumentCreationReviewView
      titleValue={titleValue}
      styleValue={styleValue}
      outlineLines={outlineLines}
      selectedSourceDocuments={selectedSourceDocuments}
    />
  );
  const snapshotView = (
    <DocumentCreationSnapshotView
      titleValue={titleValue}
      outlineCount={outlineLines.length}
      styleValue={styleValue}
      selectedSourceDocuments={selectedSourceDocuments}
    />
  );

  return (
    <DocumentCreationPanel
      steps={steps}
      stepIndex={stepIndex}
      stepPhase={stepPhase}
      backPhase={backPhase}
      formView={formView}
      reviewView={reviewView}
      nextActionLabel={nextActionLabel}
      nextAction={nextAction}
      nextDisabled={nextDisabled}
      onBack={onBack}
      runIO={runPanelIO}
      snapshotView={snapshotView}
    />
  );
};

export default DocumentCreation;
